import Link from "next/link";
import { useRouter } from "next/router";
import clsx from "clsx";

import { Card } from "@chef/components";
import { BRAND_PATHS } from "@chef/constants";
import { Breakfast, Dinner } from "@chef/icons/small";
import { Cart } from "@chef/icons/large";
import { useTrack } from "@chef/state-management/hooks";
import { isEqualStrings } from "@chef/utils/equal";
import { DeviationIndicator } from "@chef/smart-components";

import { intl } from "./MyMenuVariant.Intl";

type LinkWithIconProps = {
  label: string;
  href: string;
  isActive: boolean;
  children: React.ReactNode;
  id: string;
};

export const LinkWithIcon = ({
  label,
  href,
  isActive,
  children,
  id,
}: LinkWithIconProps) => {
  const track = useTrack();

  const handleClick = (label: string, href: string) => {
    track("linkClicked", {
      affiliation: "Frontend process",
      link_action: "click",
      link_destination: href,
      link_placement: "bottom_tab_bar",
      link_source: "code",
      link_text: label,
    });
  };

  return (
    <Link
      key={label}
      href={href}
      passHref
      className="relative col-span-1 text-center"
      id={id}
      onClick={() => handleClick(label, href)}
    >
      <div className={clsx(isActive ? "opacity-100" : "opacity-60")}>
        <div
          className={clsx(
            isActive && "opacity-100",
            "w-10 h-10 m-auto align-bottom content-center",
          )}
        >
          {children}
        </div>

        <div className="text-xs">{label}</div>
      </div>
    </Link>
  );
};

export const MyMenuVariant = () => {
  const { asPath, query } = useRouter();

  const { week: _week, year: _year } = query;
  const week = Number(_week);
  const year = Number(_year);

  return (
    <Card
      noPadding
      className="fixed bottom-0 left-0 right-0 z-20 w-full py-2 px-9 print:hidden md:hidden"
    >
      <nav
        className="grid content-center w-full h-full grid-cols-3 text-center"
        id="tab-bar"
      >
        <LinkWithIcon
          label={intl.MEALS}
          id="bottom_tab_bar_dishes"
          href={BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year })}
          isActive={[
            BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year }),
            BRAND_PATHS.MY_MENU_PAGE,
          ].some((link) => isEqualStrings(asPath, link))}
        >
          <Dinner className="w-6 h-6" />
        </LinkWithIcon>

        <LinkWithIcon
          label={intl.ADDONS}
          id="bottom_tab_bar_addons"
          href={BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year })}
          isActive={isEqualStrings(
            asPath,
            BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year }),
          )}
        >
          <Breakfast className="w-6 h-6" />
        </LinkWithIcon>

        <LinkWithIcon
          label={intl.ORDER}
          id="bottom_tab_bar_checkout"
          href={BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE({ week, year })}
          isActive={isEqualStrings(
            asPath,
            BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE({ week, year }),
          )}
        >
          <>
            <DeviationIndicator
              week={week}
              year={year}
              className="absolute translate-x-1 -translate-y-1 left-1/2"
            />
            <Cart className="w-6 h-6" />
          </>
        </LinkWithIcon>
      </nav>
    </Card>
  );
};
